import React, { useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import ManagedInput from 'components/form/managed_input';
import { Modal } from 'react-bootstrap-5';
import Button from '../shared/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';

const textContent = {
  en: {
    title: 'Your Privacy is important to us',
    body: `Buildout is committed to protecting and respecting your privacy in the US. We will only use your
          personal information to administer your account and to provide services you request from us.
          For more information on our privacy practices and how we are committed to protecting your
          personal information, please review our `,
    privacyPolicyLinkText: 'privacy policy',
    contact: (name: string) => `Contact ${name} if you have any concerns`,
    agreeButtonText: 'I Agree and accept',
    doNotSellLinkText: 'Do not sell or share my personal information'
  },
  fr: {
    title: 'Votre vie privée est importante pour nous.',
    body: `Buildout s’engage à protéger et à respecter votre vie privée aux États-Unis. Nous utiliserons vos
           informations personnelles uniquement pour gérer votre compte et fournir les services que vous nous
           demandez. Pour plus d’informations sur notre politique de confidentialité et sur la manière dont
           nous nous engageons à protéger vos données personnelles, veuillez consulter notre `,
    privacyPolicyLinkText: 'politique de confidentialité',
    contact: (name: string) => `Contactez ${name} si vous avez des préoccupations`,
    agreeButtonText: 'J’accepte et je donne mon consentement',
    doNotSellLinkText: 'Ne pas vendre ni partager mes informations personnelles.'
  }
};

type Props = {
  companyName: string,
  cookieName: string,
};

export default function EuPrivacyPolicy({ companyName, cookieName }: Props) {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(hasAcceptedPrivacyPolicy);
  const [language, setLanguage] = useState<string>(navigator.language.includes('fr') ? 'fr' : 'en');
  const content = useMemo(() => textContent[language], [language]);

  function hasAcceptedPrivacyPolicy() {
    const cookie = Cookies.get(cookieName);
    return !cookie;
  }

  function acceptPrivacyPolicy() {
    setIsModalOpen(false);
    Cookies.set(cookieName, 'true', { expires: 365 });
  }

  return (
    <Modal show={isModalOpen} size="sm">
      <Modal.Body className="p-5">
        <div className="text-center">
          <FontAwesomeIcon className="icon-gradient" icon={faCircleInfo} size="4x"/>
        </div>
        <h2 className="text-center mt-5 mb-4"><b>{content.title}</b></h2>
        <div className="text-center mb-4">
          <ManagedInput
            className="form-select w-auto d-inline-block"
            name="language-select"
            type="select"
            value={language}
            onValueChange={setLanguage}
          >
            <option value="en">🏴󠁧󠁢󠁥󠁮󠁧󠁿 English</option>

            <option value="fr">🇫🇷 Français</option>
          </ManagedInput>
        </div>
        <p style={{ fontSize: '15px' }}>
          {content.body}
          <a
            href="https://www.buildout.com/terms-of-use"
            rel="noopener noreferrer"
            target="_blank"
          >
            {content.privacyPolicyLinkText}.
          </a>
        </p>
        <p className="mb-5 mt-4" style={{ fontSize: '15px' }}>{content.contact(companyName)}</p>
        <div className="text-center my-5">
          <Button.Primary
            name="privacy-agree"
            type="button"
            onClick={acceptPrivacyPolicy}
          >
            {content.agreeButtonText}
          </Button.Primary>
        </div>
        <a
          className="d-block text-center"
          href="https://buildout.com/privacy_requests/new"
          rel="noopener noreferrer"
          target="_blank"
        >
          {content.doNotSellLinkText}
        </a>
      </Modal.Body>
    </Modal>
  );
}
